/**
 * The hex board every surface draws, filling whatever it is put in.
 *
 * The tiling comes from the sdk, so this and the platform's hit test are the same shape.
 */

import { hexgrid } from "@nebulize/sdk";
import { useEffect, useRef, useState } from "react";

export type BoardLook = "broadcast" | "control";

export interface HexState {
  picks: number;
  selected: boolean;
  blocked: boolean;
}

export interface HexBoardProps {
  columns: number;
  rows: number;
  cells: Map<string, HexState>;
  look: BoardLook;
  showCounts?: boolean;
  onPick?: (id: string) => void;
  picked?: string | null;
}

interface Size {
  width: number;
  height: number;
}

/** Pixel size of the element, watched so the board re-lays out when the window changes. */
function useSize(): [React.RefObject<HTMLDivElement | null>, Size] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;
    if (element === null) return;
    const watch = new ResizeObserver(() => {
      setSize({ width: element.clientWidth, height: element.clientHeight });
    });
    watch.observe(element);
    return () => watch.disconnect();
  }, []);

  return [ref, size];
}

interface Box extends Size {
  left: number;
  top: number;
}

/** The shape a board of regular hexes wants: wider than tall, and set by the tiling. */
function naturalAspect(columns: number, rows: number): number {
  const tall = (2 / Math.sqrt(3)) * (hexgrid.ROW_PITCH * rows + hexgrid.SHOULDER);
  return tall > 0 ? columns / tall : 1;
}

/** Where the board sits inside the element, and how big. Control keeps its shape; broadcast fills. */
function fitted(size: Size, columns: number, rows: number, look: BoardLook): Box {
  if (look === "broadcast") return { ...size, left: 0, top: 0 };

  const natural = naturalAspect(columns, rows);
  const wide = size.width / size.height > natural;
  const width = wide ? size.height * natural : size.width;
  const height = wide ? size.height : size.width / natural;
  return {
    width,
    height,
    left: (size.width - width) / 2,
    top: (size.height - height) / 2,
  };
}

const LOOKS = {
  broadcast: {
    background: "transparent",
    line: "rgba(255,255,255,0.16)",
    lineSelected: "rgba(255,214,0,0.55)",
    fill: "transparent",
    fillSelected: "rgba(255,214,0,0.20)",
    fillBlocked: "transparent",
    text: "rgba(255,255,255,0.30)",
    textLoud: "rgba(255,214,0,0.85)",
    weight: 1,
  },
  control: {
    background: "#0b0d10",
    line: "rgba(255,255,255,0.22)",
    lineSelected: "#ffd600",
    fill: "rgba(255,255,255,0.03)",
    fillSelected: "rgba(255,214,0,0.55)",
    fillBlocked: "#000000",
    text: "rgba(255,255,255,0.45)",
    textLoud: "#ffffff",
    weight: 1.5,
  },
} as const;

export function HexBoard({
  columns,
  rows,
  cells,
  look,
  showCounts = true,
  onPick,
  picked = null,
}: HexBoardProps) {
  const [ref, size] = useSize();
  const paint = LOOKS[look];
  const ready = size.width > 0 && size.height > 0;
  const board = fitted(size, columns, rows, look);

  return (
    <div
      ref={ref}
      style={{
        position: "absolute",
        inset: 0,
        background: paint.background,
        overflow: "hidden",
      }}
    >
      {ready && (
        <svg
          width={board.width}
          height={board.height}
          style={{
            display: "block",
            position: "absolute",
            left: board.left,
            top: board.top,
          }}
        >
          {hexgrid.cells(columns, rows).map(({ row, column }) => {
            const id = hexgrid.cellId(row, column);
            const held = cells.get(id);
            const points = hexgrid
              .vertices(columns, rows, row, column)
              .map((one) => `${one.x * board.width},${one.y * board.height}`)
              .join(" ");
            const at = hexgrid.center(columns, rows, row, column);
            const votes = held?.picks ?? 0;
            const isMine = picked === id;
            const fill = held?.blocked
              ? paint.fillBlocked
              : held?.selected
                ? paint.fillSelected
                : paint.fill;

            return (
              <g
                key={id}
                onClick={onPick === undefined ? undefined : () => onPick(id)}
                style={{ cursor: onPick === undefined ? "default" : "pointer" }}
              >
                <polygon
                  points={points}
                  fill={fill}
                  stroke={held?.selected ? paint.lineSelected : paint.line}
                  strokeWidth={isMine ? paint.weight * 2.5 : paint.weight}
                />
                {showCounts && (
                  <text
                    x={at.x * board.width}
                    y={at.y * board.height}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontFamily="ui-monospace, monospace"
                    fontSize={Math.max(9, Math.min(board.width / columns / 3.2, 22))}
                    fill={votes > 0 ? paint.textLoud : paint.text}
                    style={{ pointerEvents: "none", userSelect: "none" }}
                  >
                    {votes > 0 ? votes : ""}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}
